import {
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
} from '@cloudscape-design/components';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import { useTheme } from '../../features/theme/themeProvider';
import {
  buildChartOptions,
  buildTm2Datasets,
  buildTm3Datasets,
  createVerticalLinePlugin,
  DARK_MODE_BORDER_COLOR,
  TM3_PROCESS_SERIES,
} from './chartShared';
import { useProcessData } from './processDataProvider';

function ProcessGraph() {
  const {
    instronData,
    plcData,
    isTM3,
    isPlcDataLoading,
    offset,
    setOffset,
    session,
  } = useProcessData();
  const { isDarkMode } = useTheme();

  const data = useMemo(() => {
    if (!plcData || plcData.length === 0) {
      return { labels: [], datasets: [] };
    }

    let temps;

    if (isTM3) {
      temps = buildTm3Datasets({
        plcData,
        series: TM3_PROCESS_SERIES,
        processName: session?.process,
        defaultVisibleLabels: ['Top Temp', 'Bottom Temp', 'Vacuum Zone'],
      });
    } else {
      temps = buildTm2Datasets({
        plcData,
        defaultVisibleLabels: ['Top Temp', 'Bottom Temp', 'Vacuum'],
      });
    }

    const forces = instronData?.map((instron, i) => ({
      label: `Force${i > 0 ? ` ${i + 1}` : ''}`,
      data: instron.timestamps.map((timestamp, j) => ({ timestamp, force: instron.forces[j] })),
      borderColor: 'green',
      backgroundColor: 'green',
      yAxisID: 'y1',
      pointStyle: false,
      tension: 0.1,
      borderWidth: 2,
      tooltip: {
        callbacks: {
          label(context) {
            return `Force: ${context.raw.force.toFixed(1)} kN`;
          },
        },
      },
      parsing: {
        xAxisKey: 'timestamp',
        yAxisKey: 'force',
      },
    }));

    const displacements = instronData?.map((instron, i) => ({
      label: `Displacement${i > 0 ? ` ${i + 1}` : ''}`,
      data: instron.timestamps.map(
        (timestamp, j) => ({ timestamp, displacement: instron.displacements[j] }),
      ),
      borderColor: 'darkcyan',
      backgroundColor: 'darkcyan',
      yAxisID: 'y2',
      pointStyle: false,
      tension: 0.1,
      borderWidth: 2,
      hidden: true,
      tooltip: {
        callbacks: {
          label(context) {
            return `Displacement: ${context.raw.displacement.toFixed(1)} mm`;
          },
        },
      },
      parsing: {
        xAxisKey: 'timestamp',
        yAxisKey: 'displacement',
      },
    }));

    return {
      labels: plcData.map(({ timestamp }) => timestamp),
      datasets: [...(forces || []), ...(displacements || []), ...temps],
    };
  }, [plcData, instronData, isTM3, session?.process]);

  const plugin = useMemo(() => createVerticalLinePlugin(), []);

  const options = useMemo(() => {
    const startTime = instronData?.[0]?.startTime ? new Date(instronData?.[0].startTime) : null;
    if (isTM3) {
      const opts = buildChartOptions({
        isDarkMode,
        isLoading: isPlcDataLoading,
        startTime,
      });
      const isDatasetActive = (chart, label) => chart.data.datasets.some(
        (dataset) => dataset.label === label && !dataset.hidden,
      );

      if (instronData?.[0]?.timestamps?.length > 0) {
        opts.scales.y1 = {
          title: { text: 'Force (kN)', display: true },
          type: 'linear',
          display: true,
          position: 'right',
          grid: {
            drawOnChartArea: false,
          },
        };
        opts.scales.y2 = {
          title: { text: 'Displacement (mm)', display: true },
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
          },
        };
      }

      opts.plugins.legend.onClick = (e, legendItem, legend) => {
        const { chart } = legend;
        const { datasets } = chart.data;
        const index = legendItem.datasetIndex;
        const clickedLabel = datasets[index].label;
        const forceLabel = 'Force';
        const displacementLabel = 'Displacement';

        datasets.forEach((dataset, datasetIndex) => {
          if (datasetIndex === index) {
            datasets[datasetIndex] = { ...dataset, hidden: !dataset.hidden };
          } else if (
            (clickedLabel === forceLabel && dataset.label === displacementLabel)
            || (clickedLabel === displacementLabel && dataset.label === forceLabel)
          ) {
            datasets[datasetIndex] = { ...dataset, hidden: true };
          }
        });

        if (chart.options.scales.y1) {
          chart.options.scales.y1.display = isDatasetActive(chart, forceLabel);
        }
        if (chart.options.scales.y2) {
          chart.options.scales.y2.display = isDatasetActive(chart, displacementLabel);
        }

        chart.update();
      };

      return opts;
    }

    const getSecondsLabel = (timestamp) => {
      if (!startTime) return null;
      const time = new Date(timestamp).getTime();
      const diffInSeconds = Math.round((time - startTime) / 1000);

      const minutes = Math.floor(Math.abs(diffInSeconds) / 60);
      const seconds = Math.abs(diffInSeconds) % 60;
      const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

      return diffInSeconds < 0 ? `-${formattedTime}` : formattedTime;
    };
    const isDatasetActive = (chart, label) => chart.data.datasets.some(
      (dataset) => dataset.label === label && !dataset.hidden,
    );
    const opts = {
      interaction: {
        mode: 'x',
        intersect: false,
      },
      stacked: false,
      plugins: {
        verticalLine: {
          color: isDarkMode ? 'white' : 'black',
        },
        tooltip: {
          animation: false,
          // Only show the first instance of each dataset in the tooltip.
          // TODO: Figure out if there's a more efficient way to do this.
          // The current complexity is O(n^2) because filter is called for
          // each element in the array.
          filter: (element, index, array) => {
            const firstIndexWithLabel = {};
            for (let i = 0; i < array.length; i += 1) {
              if (firstIndexWithLabel[array[i].dataset.label] === undefined) {
                firstIndexWithLabel[array[i].dataset.label] = i;
              }
            }
            return firstIndexWithLabel[element.dataset.label] === index;
          },
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length > 0) {
                const timestamp = tooltipItems[0].parsed.x;
                const secLabel = getSecondsLabel(timestamp);
                return secLabel ? `${getSecondsLabel(timestamp)} (${new Date(timestamp).toLocaleString()})` : new Date(timestamp).toLocaleString();
              }
              return '';
            },
          },
        },
        legend: {
          position: 'right',
          onClick: (e, legendItem, legend) => {
            const { chart } = legend;
            const { datasets } = chart.data;
            const index = legendItem.datasetIndex;

            // Get the dataset label
            const clickedLabel = datasets[index].label;

            // Define Force and Displacement labels (update these if different)
            const forceLabel = 'Force';
            const displacementLabel = 'Displacement';

            datasets.forEach((dataset, i) => {
              if (i === index) {
                // Toggle clicked dataset
                datasets[i] = { ...dataset, hidden: !dataset.hidden };
              } else if (
                (clickedLabel === forceLabel && dataset.label === displacementLabel)
                || (clickedLabel === displacementLabel && dataset.label === forceLabel)
              ) {
                // Hide the other dataset
                datasets[i] = { ...dataset, hidden: true };
              }
            });

            // Update the y-axis visibility
            if (chart.options.scales.y1) {
              chart.options.scales.y1.display = isDatasetActive(chart, forceLabel);
            }
            if (chart.options.scales.y2) {
              chart.options.scales.y2.display = isDatasetActive(chart, displacementLabel);
            }

            chart.update();
          },
        },
      },
      animation: {
        duration: 0,
      },
      scales: {
        y: {
          title: { text: 'Temperature (°C)', display: true },
          type: 'linear',
          display: true,
          position: 'left',
          ...(isDarkMode && {
            grid: {
              color: DARK_MODE_BORDER_COLOR,
            },
            border: {
              color: DARK_MODE_BORDER_COLOR,
            },
          }),
        },
        x: {
          title: { text: 'Time', display: true },
          type: 'time',
          time: {
            unit: 'minute',
            stepSize: 1,
            displayFormats: {
              minute: 'mm:ss',
            },
          },
          ticks: {
            callback: (value) => {
              if (startTime) {
                return getSecondsLabel(value);
              }
              return new Date(value).toLocaleTimeString();
            },
          },
          ...(isDarkMode && {
            grid: {
              color: DARK_MODE_BORDER_COLOR,
            },
          }),
        },
      },
    };

    if (isPlcDataLoading) {
      opts.plugins.annotation = {
        annotations: {
          annotation: {
            type: 'box',
            backgroundColor: 'transparent',
            borderWidth: 0,
            label: {
              drawTime: 'afterDatasetsDraw',
              display: true,
              color: 'rgba(208, 208, 208, 0.5)',
              content: 'Loading...',
              font: {
                size: (ctx) => ctx.chart.chartArea.height / 4,
              },
              position: 'center',
            },
          },
        },
      };
    }

    if (instronData?.[0]?.timestamps?.length > 0) {
      opts.scales.y1 = {
        title: { text: 'Force (kN)', display: true },
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ...(isDarkMode && {
          border: {
            color: DARK_MODE_BORDER_COLOR,
          },
        }),
      };
      opts.scales.y2 = {
        title: { text: 'Displacement (mm)', display: true },
        type: 'linear',
        display: false,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ...(isDarkMode && {
          border: {
            color: DARK_MODE_BORDER_COLOR,
          },
        }),
      };
    }

    return opts;
  }, [instronData, isDarkMode, isPlcDataLoading, isTM3]);

  return (
    <Container
      header={(
        <Header actions={(
          <SpaceBetween direction="horizontal">
            <FormField label="Process window (in minutes)">
              <Input
                onChange={({ detail }) => {
                  setOffset(parseInt(detail.value, 10));
                }}
                value={offset}
                type="number"
                inputMode="numeric"
              />
            </FormField>
          </SpaceBetween>
        )}
        >
          Process Graph
        </Header>
      )}
    >
      <Line options={options} data={data} plugins={[plugin]} />
    </Container>
  );
}

export default ProcessGraph;
